// S'inscrire à Radar par e-mail (issue #182), et activer le compte par le lien
// reçu. L'autre voie, « Continuer avec Google », est sur l'écran de connexion.
//
// Le compte n'existe qu'au clic sur le lien : le formulaire ne fait qu'envoyer
// ce lien, et dit toujours la même chose, que l'adresse ait déjà un compte ou
// non (le serveur écrit à l'adresse pour le lui dire). Turnstile n'apparaît que
// si le serveur a une clé de site.

import { useEffect, useRef, useState } from 'react'
import { Eye, EyeOff, MailCheck, UserPlus } from 'lucide-react'
import { ApiError, post } from '../lib/api'
import { useLang } from '../lib/lang'
import { useT } from '../lib/i18n'

/** Doit valoir `LONGUEUR_MOT_DE_PASSE` de `web/auth.ts` : le serveur reste l'autorité. */
const LONGUEUR_MINIMALE = 12

declare global {
  interface Window {
    turnstile?: { render: (el: HTMLElement, o: { sitekey: string; callback: (jeton: string) => void; 'expired-callback': () => void }) => string }
  }
}

/** Le widget Turnstile de Cloudflare, chargé seulement quand une clé existe. */
function Turnstile({ cle, onJeton }: { cle: string; onJeton: (j: string) => void }) {
  const ici = useRef<HTMLDivElement>(null)
  useEffect(() => {
    let annule = false
    const poser = () => {
      if (annule || !ici.current || !window.turnstile) return
      window.turnstile.render(ici.current, { sitekey: cle, callback: onJeton, 'expired-callback': () => onJeton('') })
    }
    if (window.turnstile) poser()
    else {
      const s = document.createElement('script')
      s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
      s.async = true
      s.onload = poser
      document.head.appendChild(s)
    }
    return () => {
      annule = true
    }
  }, [cle, onJeton])
  return <div ref={ici} />
}

/** Le formulaire d'inscription, posé dans la carte de connexion sous l'onglet
 *  « Créer un compte ». Une fois envoyé, il dit où regarder. */
export function PanneauInscription({ turnstile }: { turnstile: string | null }) {
  const t = useT()
  const lang = useLang()
  const [email, setEmail] = useState('')
  const [motDePasse, setMotDePasse] = useState('')
  const [voir, setVoir] = useState(false)
  const [jeton, setJeton] = useState('')
  const [erreur, setErreur] = useState<string | null>(null)
  const [envoi, setEnvoi] = useState(false)
  const [parti, setParti] = useState<string | null>(null)

  async function soumettre(e: React.FormEvent) {
    e.preventDefault()
    setErreur(null)
    if (motDePasse.length < LONGUEUR_MINIMALE) {
      setErreur(t('password.tooShort', { n: LONGUEUR_MINIMALE }))
      return
    }
    if (turnstile && !jeton) {
      setErreur(t('inscription.robot'))
      return
    }
    setEnvoi(true)
    try {
      await post('/api/auth/inscription', { email, password: motDePasse, lang, ...(turnstile ? { turnstile: jeton } : {}) })
      setParti(email.trim())
    } catch (err) {
      setErreur(err instanceof ApiError ? err.message : t('login.error.generic'))
    } finally {
      setEnvoi(false)
    }
  }

  if (parti) {
    return (
      <div className="loginform" role="status">
        <p className="loginok"><MailCheck size={18} aria-hidden="true" /> {t('inscription.parti.texte', { email: parti })}</p>
        <button type="button" className="linkbtn" onClick={() => setParti(null)}>{t('inscription.autreAdresse')}</button>
      </div>
    )
  }

  return (
    <form className="loginform" onSubmit={soumettre}>
      <label className="field-label" htmlFor="insc-email">{t('login.email')}</label>
      <input id="insc-email" className="input-field" type="email" autoComplete="email" autoFocus value={email} onChange={(e) => setEmail(e.target.value)} required />
      <label className="field-label" htmlFor="insc-mdp">{t('inscription.mdp')}</label>
      <ChampMotDePasse id="insc-mdp" autoComplete="new-password" valeur={motDePasse} onChange={setMotDePasse} voir={voir} onVoir={setVoir} decritPar="insc-mdp-aide" />
      <p id="insc-mdp-aide" className="field-help">{t('inscription.mdpAide', { n: LONGUEUR_MINIMALE })}</p>
      {turnstile && <Turnstile cle={turnstile} onJeton={setJeton} />}
      {erreur && <p className="loginerr" role="alert">{erreur}</p>}
      <button className="btn-primary" type="submit" disabled={envoi}>
        <UserPlus size={16} />
        {envoi ? t('inscription.envoi') : t('inscription.envoyer')}
      </button>
      <p className="field-help logincentre">{t('inscription.essai')}</p>
    </form>
  )
}

/** Un mot de passe, avec le bouton qui l'affiche ou le masque. */
export function ChampMotDePasse({
  id,
  autoComplete,
  valeur,
  onChange,
  voir,
  onVoir,
  decritPar,
}: {
  id: string
  autoComplete: string
  valeur: string
  onChange: (v: string) => void
  voir: boolean
  onVoir: (v: boolean) => void
  decritPar?: string
}) {
  const t = useT()
  return (
    <div className="loginmdp">
      <input
        id={id}
        className="input-field"
        type={voir ? 'text' : 'password'}
        autoComplete={autoComplete}
        value={valeur}
        onChange={(e) => onChange(e.target.value)}
        aria-describedby={decritPar}
        required
      />
      <button type="button" className="loginvoir" aria-pressed={voir} aria-label={voir ? t('login.mdp.masquer') : t('login.mdp.voir')} onClick={() => onVoir(!voir)}>
        {voir ? <EyeOff size={16} /> : <Eye size={16} />}
      </button>
    </div>
  )
}

/** Le lien reçu par mail : crée le compte et l'ouvre, une seule fois. */
export function ConfirmerInscription({ token, onCree }: { token: string; onCree: () => void }) {
  const t = useT()
  const [erreur, setErreur] = useState<string | null>(null)
  // Une seule consommation : le lien ne vaut qu'une fois, et React rejoue les
  // effets en développement.
  const fait = useRef(false)
  useEffect(() => {
    if (fait.current) return
    fait.current = true
    post('/api/auth/inscription/confirmer', { token }).then(onCree, (err) =>
      setErreur(err instanceof ApiError && err.status === 410 ? t('inscription.lienInvalide') : t('login.error.generic'))
    )
  }, [token, onCree, t])
  return (
    <div className="loginwrap">
      <div className="card logincard">
        <h1 className="loginh">AI Job Fit</h1>
        {erreur ? (
          <>
            <p className="loginerr" role="alert">{erreur}</p>
            <a className="btn-secondary" href="#/">{t('login.submit')}</a>
          </>
        ) : (
          <p className="loginsub">{t('inscription.activation')}</p>
        )}
      </div>
    </div>
  )
}
